import { Component, DestroyRef, HostListener, inject, OnInit, ViewChild, ViewContainerRef } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router'
import { registerContractSerializers } from '@ftr/contracts/shared/registerContractSerializers'
import { CourtSystemsState, FetchCourtSystemPersonalizationAction } from '@ftr/data-court-system'
import { DesignSystemOverlayComponent } from '@ftr/forms'
import {
  GlobalEventsService,
  isInDesktopApp,
  isNotNullOrUndefined,
  PageTitleService,
  RemoteData,
} from '@ftr/foundation'
import { FeatureFlagState, LaunchDarklyService } from '@ftr/ui-feature-flags'
import { LoggingService } from '@ftr/ui-observability'
import { DesktopApiService } from '@ftr/ui-playback'
import { AnalyticsService, AuthenticationService, UserState } from '@ftr/ui-user'
import { FetchVocabularyTermsAction } from '@ftr/ui-vocab'
import { RecaptchaService } from '@ftr/window'
import { Store } from '@ngxs/store'
import { distinctUntilChanged, filter, first, map, Observable, of, switchMap, tap } from 'rxjs'
import { BootstrapNavigationService } from './services/bootstrap-navigation/bootstrap-navigation.service'

registerContractSerializers()

/*
 * App Component
 * Top Level Component
 */
@Component({
  selector: 'ftr-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false,
})
export class AppComponent implements OnInit {
  @ViewChild('designSystemOverlayContainer', { read: ViewContainerRef }) designSystemOverlayContainer: ViewContainerRef

  readonly isInDesktopApp = isInDesktopApp()

  logoData: RemoteData<string | undefined> = RemoteData.notAsked()
  routeData: Data | undefined
  status: 'INIT' | 'READY' | 'LOADING' = 'INIT'

  isTippyShown: boolean

  showNavigationRedesign: boolean = false

  readonly #destroyRef = inject(DestroyRef)

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly bootstrapNavigationService: BootstrapNavigationService,
    private readonly analyticsService: AnalyticsService,
    private readonly authenticationService: AuthenticationService,
    private readonly loggingService: LoggingService,
    private readonly pageTitleService: PageTitleService,
    private readonly recaptchaService: RecaptchaService,
    private readonly store: Store,
    private readonly globalEventsService: GlobalEventsService,
    private readonly desktopApiService: DesktopApiService,
    private readonly launchDarklyService: LaunchDarklyService,
  ) {}
  isPublicNavigationEnabled$: Observable<boolean>

  @HostListener('document:click', ['$event'])
  @HostListener('document:focusin', ['$event'])
  handleEvent(event: MouseEvent | FocusEvent): void {
    this.globalEventsService.registerEvent(event)
  }

  ngOnInit(): void {
    this.bootstrapApp()
    this.analyticsService.register()
    this.desktopApiService.initialize()
    this.pageTitleService.initialize()
    this.authenticationService
      .initialize()
      .then(() => this.initDesignSystemOverlayFeature())
      .catch(error => {
        this.loggingService.warn({
          message: 'Failed to initialize authentication service',
          error,
        })
      })
    this.recaptchaService.loadRecaptchaScript()

    // This will automatically fetch new vocabulary terms and any personalziation for the court system
    // on switching court systems
    this.store
      .select(UserState.currentCourtSystem)
      .pipe(filter(isNotNullOrUndefined))
      .subscribe(courtSystem => {
        const courtSystemId = courtSystem.id
        this.store.dispatch([
          new FetchVocabularyTermsAction(courtSystemId),
          new FetchCourtSystemPersonalizationAction(courtSystemId),
        ])
      })

    this.isPublicNavigationEnabled$ = this.store.select(FeatureFlagState.featureFlag('public-navigation'))

    this.store
      .select(UserState.currentCourtSystem)
      .pipe(
        switchMap(courtSystem => {
          return courtSystem
            ? this.store.select(CourtSystemsState.courtSystemPersonalization).pipe(map(f => f(courtSystem.id)))
            : of(RemoteData.success(undefined))
        }),
      )
      .subscribe(config => {
        this.logoData = config ? config.map<string | undefined>(p => p?.logoUrl) : RemoteData.success(undefined)
      })

    this.handleSecurityPolicyViolation()
  }

  private initDesignSystemOverlayFeature(): void {
    this.launchDarklyService
      .observeFlagChanges<boolean>('design-system-overlay')
      .pipe(
        distinctUntilChanged(),
        filter(designSystemOverlayEnabled => designSystemOverlayEnabled === true),
        first(),
      )
      .subscribe(() => {
        this.designSystemOverlayContainer.createComponent(DesignSystemOverlayComponent)
      })
  }

  private bootstrapApp(): void {
    // Bootstrap navigation
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        tap(route => {
          while (route.firstChild) {
            if (route.routeConfig?.canActivate) {
              return this.bootstrapNavigationService.loadData()
            }
            route = route.firstChild
          }
          this.bootstrapNavigationService.emitAppIsReady()
        }),
        switchMap(route => {
          while (route.firstChild) {
            route = route.firstChild
          }

          return route.data
        }),
      )
      .subscribe(data => (this.routeData = data))

    // Prepare app display
    this.store
      .select(UserState.user)
      .pipe(
        map(userState => {
          const newStatus = userState.isLoading() ? ('LOADING' as const) : ('READY' as const)

          return {
            status: newStatus,
            // We only want to show new navigation for logged-in pages (e.g. when we have an active user)
            showNavigationRedesign:
              newStatus === 'READY' && this.authenticationService.getCurrentUserId() !== undefined,
          }
        }),
        tap(({ status, showNavigationRedesign }) => {
          this.status = status
          this.showNavigationRedesign = showNavigationRedesign
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe()
  }

  private handleSecurityPolicyViolation(): void {
    window.addEventListener('securitypolicyviolation', (event: SecurityPolicyViolationEvent) => {
      const violation = {
        isTrusted: event.isTrusted,
        blockedURI: event.blockedURI,
        disposition: event.disposition,
        documentURI: event.documentURI,
        effectiveDirective: event.effectiveDirective,
        lineNumber: event.lineNumber,
        referrer: event.referrer,
        sample: event.sample,
        sourceFile: event.sourceFile,
        violatedDirective: event.violatedDirective,
      }
      if (window.location.hostname !== 'localhost') {
        this.loggingService.info({
          message: 'SecurityPolicyViolation',
          violation,
        })
      }
    })
  }

  showTippy(): void {
    this.isTippyShown = true
  }
}
